import { useState, useEffect, useCallback } from "react";
import { Button } from "@app/components/shared/Button";
import { SectionHeader } from "@app/components/shared/SectionHeader";
import {
  getPositionsMonitor,
  executePositionAction,
  convertUsersDust,
  getDustAccountOptions,
  type PositionsMonitorResponse,
  type PositionRow,
  type UserSection,
  type PositionStatus,
  type PositionAction,
  type ConvertDustResponse,
  type DustUserOption,
  type DustAccountOption,
  type DustAccountsListResponse,
  STATUS_LABELS,
  STATUS_COLORS,
  ACTION_LABELS,
  ACTION_COLORS,
  SOURCE_LABELS,
  SOURCE_COLORS,
} from "@app/services/adminPositionsMonitorApi";

type ViewMode = "all" | "admin" | "subscribers" | "stuck";
type GroupedMode = "grouped" | "flat";

export function AdminPositionsMonitorTab(): JSX.Element {
  const [data, setData] = useState<PositionsMonitorResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<ViewMode>("all");
  const [groupedMode, setGroupedMode] = useState<GroupedMode>("grouped");
  const [expandedUsers, setExpandedUsers] = useState<Set<string>>(new Set());
  const [executingAction, setExecutingAction] = useState<{ positionId: string; action: PositionAction } | null>(null);
  const [symbolFilter, setSymbolFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState<PositionStatus | "">("");
  const [selectedAccountIds, setSelectedAccountIds] = useState<Set<string>>(new Set());
  const [isConvertingDust, setIsConvertingDust] = useState(false);
  const [dustConvertResult, setDustConvertResult] = useState<ConvertDustResponse | null>(null);

  // Dust modal state
  const [dustModalOpen, setDustModalOpen] = useState(false);
  const [dustModalData, setDustModalData] = useState<DustAccountsListResponse | null>(null);
  const [dustModalLoading, setDustModalLoading] = useState(false);
  const [dustModalSelected, setDustModalSelected] = useState<Set<string>>(new Set());

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await getPositionsMonitor();
      setData(result);
      if (result.admin_positions.length > 0) {
        setExpandedUsers(new Set(result.admin_positions.map(u => u.account_id)));
      }
    } catch (err: any) {
      setError(err.message || "Failed to fetch positions");
    } finally {
      setIsLoading(false);
    }
  };

  const handleAction = async (position: PositionRow, action: PositionAction) => {
    if (!confirm(`Are you sure you want to "${ACTION_LABELS[action]}" for ${position.symbol}?`)) {
      return;
    }
    
    setExecutingAction({ positionId: position.position_id, action });
    try {
      const result = await executePositionAction(
        position.position_id,
        action,
        "admin manual action",
        position.data_sources,
      );
      if (result.success) {
        alert(`Success: ${result.message}`);
        await fetchData();
      } else {
        alert(`Failed: ${result.message}`);
      }
    } catch (err: any) {
      alert(`Error: ${err.message}`);
    } finally {
      setExecutingAction(null);
    }
  };

  const toggleUserExpand = (accountId: string) => {
    setExpandedUsers(prev => {
      const next = new Set(prev);
      if (next.has(accountId)) {
        next.delete(accountId);
      } else {
        next.add(accountId);
      }
      return next;
    });
  };

  const toggleExpandAll = () => {
    if (!data) return;
    if (expandedUsers.size === 0) {
      setExpandedUsers(new Set([
        ...data.admin_positions.map(u => u.account_id),
        ...data.subscriber_positions.map(u => u.account_id),
      ]));
    } else {
      setExpandedUsers(new Set());
    }
  };

  const toggleAccountSelection = (accountId: string) => {
    setSelectedAccountIds(prev => {
      const next = new Set(prev);
      if (next.has(accountId)) {
        next.delete(accountId);
      } else {
        next.add(accountId);
      }
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (!data) return;
    const allAccountIds = [
      ...data.admin_positions.map(u => u.account_id),
      ...data.subscriber_positions.map(u => u.account_id),
    ];
    if (selectedAccountIds.size === allAccountIds.length) {
      setSelectedAccountIds(new Set());
    } else {
      setSelectedAccountIds(new Set(allAccountIds));
    }
  };

  const handleConvertAllDust = async () => {
    if (!confirm("Convert dust (small balances) to USDT for ALL trading accounts?")) {
      return;
    }
    setIsConvertingDust(true);
    setDustConvertResult(null);
    try {
      const result = await convertUsersDust();
      setDustConvertResult(result);
    } catch (err: any) {
      setDustConvertResult({
        success: false,
        total_accounts: 0,
        successful_accounts: 0,
        failed_accounts: 0,
        total_coins_converted: 0,
        total_coins_remaining: 0,
        total_converted_usdt_value: 0,
        total_remaining_usdt_value: 0,
        results: [],
        message: err.message || "Conversion failed",
      });
    } finally {
      setIsConvertingDust(false);
    }
  };

  const handleConvertSelectedDust = async () => {
    if (selectedAccountIds.size === 0) {
      alert("Please select at least one account");
      return;
    }
    if (!confirm(`Convert dust (small balances) to USDT for ${selectedAccountIds.size} selected account(s)?`)) {
      return;
    }
    setIsConvertingDust(true);
    setDustConvertResult(null);
    try {
      const result = await convertUsersDust(Array.from(selectedAccountIds));
      setDustConvertResult(result);
    } catch (err: any) {
      setDustConvertResult({
        success: false,
        total_accounts: 0,
        successful_accounts: 0,
        failed_accounts: 0,
        total_coins_converted: 0,
        total_coins_remaining: 0,
        total_converted_usdt_value: 0,
        total_remaining_usdt_value: 0,
        results: [],
        message: err.message || "Conversion failed",
      });
    } finally {
      setIsConvertingDust(false);
    }
  };

  const clearDustResult = () => {
    setDustConvertResult(null);
    setSelectedAccountIds(new Set());
  };

  // Dust modal functions
  const openDustModal = useCallback(async () => {
    setDustModalOpen(true);
    setDustModalLoading(true);
    setDustModalData(null);
    setDustModalSelected(new Set());
    try {
      const result = await getDustAccountOptions();
      setDustModalData(result);
    } catch (err: any) {
      setDustModalData(null);
    } finally {
      setDustModalLoading(false);
    }
  }, []);

  const closeDustModal = () => {
    setDustModalOpen(false);
    setDustModalData(null);
    setDustModalSelected(new Set());
  };

  const toggleDustModalAccount = (accountId: string) => {
    setDustModalSelected(prev => {
      const next = new Set(prev);
      if (next.has(accountId)) {
        next.delete(accountId);
      } else {
        next.add(accountId);
      }
      return next;
    });
  };

  const toggleDustModalSelectAll = () => {
    if (!dustModalData) return;
    const allIds = new Set<string>();
    for (const user of dustModalData.admins) {
      for (const acc of user.accounts) {
        allIds.add(acc.account_id);
      }
    }
    for (const user of dustModalData.subscribers) {
      for (const acc of user.accounts) {
        allIds.add(acc.account_id);
      }
    }
    if (dustModalSelected.size === allIds.size && allIds.size > 0) {
      setDustModalSelected(new Set());
    } else {
      setDustModalSelected(allIds);
    }
  };

  const handleDustModalConvert = async () => {
    if (dustModalSelected.size === 0) {
      alert("Please select at least one account");
      return;
    }
    if (!confirm(`Convert dust (small balances) to USDT for ${dustModalSelected.size} selected account(s)?`)) {
      return;
    }
    closeDustModal();
    setIsConvertingDust(true);
    setDustConvertResult(null);
    try {
      const result = await convertUsersDust(Array.from(dustModalSelected));
      setDustConvertResult(result);
    } catch (err: any) {
      setDustConvertResult({
        success: false,
        total_accounts: 0,
        successful_accounts: 0,
        failed_accounts: 0,
        total_coins_converted: 0,
        total_coins_remaining: 0,
        total_converted_usdt_value: 0,
        total_remaining_usdt_value: 0,
        results: [],
        message: err.message || "Conversion failed",
      });
    } finally {
      setIsConvertingDust(false);
    }
  };

  const allDustModalAccountIds = (): string[] => {
    if (!dustModalData) return [];
    const ids: string[] = [];
    for (const user of dustModalData.admins) {
      for (const acc of user.accounts) {
        ids.push(acc.account_id);
      }
    }
    for (const user of dustModalData.subscribers) {
      for (const acc of user.accounts) {
        ids.push(acc.account_id);
      }
    }
    return ids;
  };

  const filterPositions = (positions: PositionRow[]) => {
    return positions.filter((pos) => {
      if (symbolFilter && !pos.symbol.toLowerCase().includes(symbolFilter.toLowerCase())) return false;
      if (statusFilter && pos.status !== statusFilter) return false;
      return true;
    });
  };

  const getPositionStatusLabel = (pos: PositionRow) =>
    pos.status === "ghost" && pos.data_sources.includes("dead_exit_jobs")
      ? "Ghost Job"
      : STATUS_LABELS[pos.status];

  const getFilteredData = () => {
    if (!data) return null;
    const filteredAdmin = data.admin_positions
      .map((section) => ({
        ...section,
        positions: filterPositions(section.positions),
      }))
      .filter((section) => section.positions.length > 0);

    const filteredSubscribers = data.subscriber_positions
      .map((section) => ({
        ...section,
        positions: filterPositions(section.positions),
      }))
      .filter((section) => section.positions.length > 0);

    const filteredStuck = filterPositions(data.stuck_positions);

    return { admin_positions: filteredAdmin, subscriber_positions: filteredSubscribers, stuck_positions: filteredStuck, stats: data.stats };
  };

  const renderStats = () => {
    if (!data) return null;
    const { stats } = data;
    
    return (
      <div style={{ display: "flex", gap: "1rem", flexWrap: "wrap", marginBottom: "1rem" }}>
        <StatCard label="Total" value={stats.total_positions} color="#3b82f6" />
        <StatCard label="Active" value={stats.active_positions} color={STATUS_COLORS.active} />
        <StatCard label="Pending" value={stats.pending_positions} color={STATUS_COLORS.pending} />
        <StatCard label="Orphaned" value={stats.orphaned_positions} color={STATUS_COLORS.orphaned} />
        <StatCard label="Stuck" value={stats.stuck_positions} color={STATUS_COLORS.stuck} />
        <StatCard label="Ghost" value={stats.ghost_positions} color={STATUS_COLORS.ghost} />
      </div>
    );
  };

  const renderUserSection = (section: UserSection, sectionType: "admin" | "subscriber") => {
    const isExpanded = expandedUsers.has(section.account_id);
    const isSelected = selectedAccountIds.has(section.account_id);
    
    return (
      <div key={section.account_id} style={{ marginBottom: "0.75rem", border: "1px solid var(--border)", borderRadius: "0.5rem" }}>
        <div
          onClick={() => toggleUserExpand(section.account_id)}
          style={{
            padding: "0.75rem",
            cursor: "pointer",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            backgroundColor: sectionType === "admin" ? "rgba(59, 130, 246, 0.05)" : "rgba(16, 185, 129, 0.05)",
            borderRadius: isExpanded ? "0.5rem 0.5rem 0 0" : "0.5rem",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
            <input
              type="checkbox"
              checked={isSelected}
              onChange={(e) => {
                e.stopPropagation();
                toggleAccountSelection(section.account_id);
              }}
              onClick={(e) => e.stopPropagation()}
              style={{ cursor: "pointer" }}
            />
            <span style={{ fontSize: "1.2rem" }}>{isExpanded ? "▼" : "▶"}</span>
            <span style={{ fontWeight: "bold" }}>
              {sectionType === "admin" ? "👤" : "👥"} {section.user_email}
            </span>
            <span style={{ fontSize: "0.8rem", color: "var(--text-secondary)" }}>
              {section.account_nickname && `(${section.account_nickname})`}
            </span>
            <span style={{
              padding: "0.15rem 0.5rem",
              borderRadius: "1rem",
              fontSize: "0.75rem",
              backgroundColor: sectionType === "admin" ? "#3b82f6" : "#10b981",
              color: "white",
            }}>
              {sectionType === "admin" ? "Admin" : "Subscriber"}
            </span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
            <span style={{ fontSize: "0.85rem" }}>
              {section.positions_count} positions
            </span>
            <span style={{ fontSize: "0.85rem", fontWeight: "bold" }}>
              PnL: {section.total_pnl >= 0 ? "+" : ""}{section.total_pnl.toFixed(2)}
            </span>
          </div>
        </div>
        
        {isExpanded && section.positions.length > 0 && (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.85rem" }}>
              <thead>
                <tr style={{ backgroundColor: "var(--background-secondary)", borderBottom: "1px solid var(--border)" }}>
                  <th style={{ padding: "0.5rem", textAlign: "left" }}>Symbol</th>
                  <th style={{ padding: "0.5rem", textAlign: "center" }}>Linked</th>
                  <th style={{ padding: "0.5rem", textAlign: "right" }}>Entry Price</th>
                  <th style={{ padding: "0.5rem", textAlign: "right" }}>Quantity</th>
                  <th style={{ padding: "0.5rem", textAlign: "right" }}>Current Price</th>
                  <th style={{ padding: "0.5rem", textAlign: "right" }}>PnL</th>
                  <th style={{ padding: "0.5rem", textAlign: "center" }}>Status</th>
                  <th style={{ padding: "0.5rem", textAlign: "right" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {section.positions.map((pos) => renderPositionRow(pos))}
              </tbody>
            </table>
          </div>
        )}
        
        {isExpanded && section.positions.length === 0 && (
          <div style={{ padding: "1rem", textAlign: "center", color: "var(--text-secondary)" }}>
            No open positions
          </div>
        )}
      </div>
    );
  };

  const renderPositionRow = (pos: PositionRow) => {
    const isExecuting = executingAction?.positionId === pos.position_id;
    const statusLabel = getPositionStatusLabel(pos);
    
    return (
      <tr key={pos.position_id} style={{ borderBottom: "1px solid var(--border)" }}>
        <td style={{ padding: "0.5rem", fontWeight: "bold" }}>{pos.symbol}</td>
        <td style={{ padding: "0.5rem", textAlign: "center" }}>
          {pos.linked_signal_id ? (
            <span style={{ padding: "0.15rem 0.5rem", borderRadius: "1rem", fontSize: "0.7rem", backgroundColor: "#3b82f6", color: "white", whiteSpace: "nowrap" }}>Yes</span>
          ) : (
            <span style={{ color: "var(--text-secondary)", fontSize: "0.75rem" }}>–</span>
          )}
        </td>
        <td style={{ padding: "0.5rem", textAlign: "right" }}>{(pos.entry_price ?? 0).toFixed(2)}</td>
        <td style={{ padding: "0.5rem", textAlign: "right" }}>{(pos.quantity ?? 0).toFixed(4)}</td>
        <td style={{ padding: "0.5rem", textAlign: "right" }}>
          {pos.current_price ? pos.current_price.toFixed(2) : "-"}
        </td>
        <td style={{
          padding: "0.5rem",
          textAlign: "right",
          fontWeight: "bold",
          color: pos.pnl != null ? (pos.pnl >= 0 ? "#10b981" : "#ef4444") : "inherit",
        }}>
          {pos.pnl != null ? `${pos.pnl >= 0 ? "+" : ""}${pos.pnl.toFixed(2)}` : "-"}
        </td>
        <td style={{ padding: "0.5rem", textAlign: "center" }}>
          <span style={{
            padding: "0.15rem 0.5rem",
            borderRadius: "1rem",
            fontSize: "0.75rem",
            backgroundColor: STATUS_COLORS[pos.status],
            color: "white",
            whiteSpace: "nowrap",
          }}>
            {statusLabel}
          </span>
          {pos.status === "stuck" && (
            <div style={{ fontSize: "0.7rem", color: "var(--error)", marginTop: "0.25rem" }}>
              {pos.exit_attempts} failed attempts
            </div>
          )}
          {pos.status_reason && pos.status !== "stuck" && (
            <div style={{ fontSize: "0.68rem", color: "var(--text-secondary)", marginTop: "0.25rem" }}>
              {pos.status_reason}
            </div>
          )}
          {(pos.data_sources ?? []).length > 0 && (
            <div style={{ marginTop: "0.35rem", display: "flex", gap: "0.2rem", flexWrap: "wrap", justifyContent: "center" }}>
              {(pos.data_sources ?? []).map((src: string) => (
                <span key={src} style={{
                  padding: "0.1rem 0.35rem",
                  borderRadius: "0.25rem",
                  fontSize: "0.6rem",
                  backgroundColor: SOURCE_COLORS[src] || "#6b7280",
                  color: "white",
                  whiteSpace: "nowrap",
                  opacity: 0.85,
                }}>
                  {SOURCE_LABELS[src] || src}
                </span>
              ))}
            </div>
          )}
        </td>
        <td style={{ padding: "0.5rem", textAlign: "right" }}>
          <div style={{ display: "flex", gap: "0.25rem", flexWrap: "wrap", justifyContent: "flex-end" }}>
            {pos.available_actions.map((action) => (
              <Button
                key={action}
                size="small"
                variant={ACTION_COLORS[action] as "primary" | "secondary" | "danger" | "neutral" | "ghost" | "link" | "accent"}
                disabled={isExecuting || executingAction !== null}
                onClick={() => handleAction(pos, action)}
              >
                {isExecuting && executingAction?.action === action ? "..." : ACTION_LABELS[action]}
              </Button>
            ))}
          </div>
        </td>
      </tr>
    );
  };

  const renderStuckPositions = (stuckPositions: PositionRow[]) => {
    if (!stuckPositions.length) {
      return (
        <div style={{ padding: "2rem", textAlign: "center", color: "var(--text-secondary)" }}>
          No stuck positions
        </div>
      );
    }

    return (
      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.85rem" }}>
          <thead>
            <tr style={{ backgroundColor: "var(--background-secondary)", borderBottom: "1px solid var(--border)" }}>
              <th style={{ padding: "0.5rem", textAlign: "left" }}>Symbol</th>
              <th style={{ padding: "0.5rem", textAlign: "center" }}>Linked</th>
              <th style={{ padding: "0.5rem", textAlign: "right" }}>Entry Price</th>
              <th style={{ padding: "0.5rem", textAlign: "right" }}>Quantity</th>
              <th style={{ padding: "0.5rem", textAlign: "left" }}>Status Reason</th>
              <th style={{ padding: "0.5rem", textAlign: "center" }}>Status</th>
              <th style={{ padding: "0.5rem", textAlign: "right" }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {stuckPositions.map((pos) => (
              <tr key={pos.position_id} style={{ borderBottom: "1px solid var(--border)", backgroundColor: "rgba(239, 68, 68, 0.05)" }}>
                <td style={{ padding: "0.5rem", fontWeight: "bold" }}>{pos.symbol}</td>
                <td style={{ padding: "0.5rem", textAlign: "center" }}>
                  {pos.linked_signal_id ? (
                    <span style={{ padding: "0.15rem 0.5rem", borderRadius: "1rem", fontSize: "0.7rem", backgroundColor: "#3b82f6", color: "white", whiteSpace: "nowrap" }}>Yes</span>
                  ) : (
                    <span style={{ color: "var(--text-secondary)", fontSize: "0.75rem" }}>–</span>
                  )}
                </td>
                <td style={{ padding: "0.5rem", textAlign: "right" }}>{(pos.entry_price ?? 0).toFixed(2)}</td>
                <td style={{ padding: "0.5rem", textAlign: "right" }}>{(pos.quantity ?? 0).toFixed(4)}</td>
                <td style={{ padding: "0.5rem", fontSize: "0.8rem", color: "var(--text-secondary)" }}>
                  {pos.status_reason}
                </td>
                <td style={{ padding: "0.5rem", textAlign: "center" }}>
                  <span style={{
                    padding: "0.15rem 0.5rem",
                    borderRadius: "1rem",
                    fontSize: "0.75rem",
                    backgroundColor: STATUS_COLORS.stuck,
                    color: "white",
                  }}>
                    {STATUS_LABELS.stuck}
                  </span>
                  <div style={{ fontSize: "0.7rem", color: "var(--error)", marginTop: "0.25rem" }}>
                    {pos.exit_attempts} failed attempts
                  </div>
                  {(pos.data_sources ?? []).length > 0 && (
                    <div style={{ marginTop: "0.35rem", display: "flex", gap: "0.2rem", flexWrap: "wrap", justifyContent: "center" }}>
                      {(pos.data_sources ?? []).map((src: string) => (
                        <span key={src} style={{
                          padding: "0.1rem 0.35rem",
                          borderRadius: "0.25rem",
                          fontSize: "0.6rem",
                          backgroundColor: SOURCE_COLORS[src] || "#6b7280",
                          color: "white",
                          whiteSpace: "nowrap",
                          opacity: 0.85,
                        }}>
                          {SOURCE_LABELS[src] || src}
                        </span>
                      ))}
                    </div>
                  )}
                </td>
                <td style={{ padding: "0.5rem", textAlign: "right" }}>
                  <div style={{ display: "flex", gap: "0.25rem", flexWrap: "wrap", justifyContent: "flex-end" }}>
                    {pos.available_actions.map((action) => (
                      <Button
                        key={action}
                        size="small"
                        variant={ACTION_COLORS[action] as "primary" | "secondary" | "danger" | "neutral" | "ghost" | "link" | "accent"}
                        disabled={executingAction !== null}
                        onClick={() => handleAction(pos, action)}
                      >
                        {executingAction?.positionId === pos.position_id && executingAction?.action === action ? "..." : ACTION_LABELS[action]}
                      </Button>
                    ))}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      <SectionHeader
        title="Positions Monitor"
        subtitle="Monitor all open positions across admin and subscriber accounts"
      />
      
      <div style={{ marginBottom: "1rem", display: "flex", gap: "0.5rem", flexWrap: "wrap", alignItems: "center" }}>
        <Button onClick={fetchData} disabled={isLoading} variant="primary">
          {isLoading ? "⟳ Loading..." : "⟳ Refresh"}
        </Button>

        <div style={{ display: "flex", gap: "0.25rem" }}>
          <Button size="small" variant={viewMode === "all" ? "primary" : "secondary"} onClick={() => setViewMode("all")}>All</Button>
          <Button size="small" variant={viewMode === "admin" ? "primary" : "secondary"} onClick={() => setViewMode("admin")}>Admin</Button>
          <Button size="small" variant={viewMode === "subscribers" ? "primary" : "secondary"} onClick={() => setViewMode("subscribers")}>Subscribers</Button>
          <Button size="small" variant={viewMode === "stuck" ? "primary" : "secondary"} onClick={() => setViewMode("stuck")}>Stuck</Button>
        </div>

        <div style={{ display: "flex", gap: "0.25rem" }}>
          <input
            type="text"
            placeholder="Filter by symbol..."
            value={symbolFilter}
            onChange={(e) => setSymbolFilter(e.target.value)}
            style={{
              padding: "0.35rem 0.5rem",
              borderRadius: "0.375rem",
              border: "1px solid var(--border)",
              fontSize: "0.8rem",
              width: "140px",
              backgroundColor: "var(--background-primary)",
              color: "var(--text-primary)",
            }}
          />
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as PositionStatus | "")}
            style={{
              padding: "0.35rem 0.5rem",
              borderRadius: "0.375rem",
              border: "1px solid var(--border)",
              fontSize: "0.8rem",
              backgroundColor: "var(--background-primary)",
              color: "var(--text-primary)",
            }}
          >
            <option value="">All Status</option>
            <option value="active">Active</option>
            <option value="pending">Pending Exit</option>
            <option value="orphaned">Orphaned</option>
            <option value="stuck">Stuck</option>
            <option value="ghost">Ghost</option>
          </select>
        </div>
        
        <div style={{ display: "flex", gap: "0.25rem", marginLeft: "auto" }}>
          <Button
            size="small"
            variant={groupedMode === "grouped" ? "primary" : "secondary"}
            onClick={() => setGroupedMode("grouped")}
          >
            Grouped
          </Button>
          <Button
            size="small"
            variant={groupedMode === "flat" ? "primary" : "secondary"}
            onClick={() => setGroupedMode("flat")}
          >
            Flat
          </Button>
        </div>
        
        {groupedMode === "grouped" && (
          <Button size="small" variant="secondary" onClick={toggleExpandAll}>
            {expandedUsers.size === 0 ? "Expand All" : "Collapse All"}
          </Button>
        )}

        <Button
          size="small"
          variant="accent"
          disabled={isConvertingDust}
          onClick={openDustModal}
          title="Convert small coin balances to USDT"
          style={{ marginLeft: groupedMode === "grouped" && data ? undefined : "auto" }}
        >
          ✨ Convert Dust
        </Button>

        {groupedMode === "grouped" && data && (
          <div style={{ display: "flex", gap: "0.25rem", alignItems: "center", borderLeft: "1px solid var(--border)", paddingLeft: "0.5rem" }}>
            <input
              type="checkbox"
              checked={selectedAccountIds.size > 0 && selectedAccountIds.size === [...data.admin_positions, ...data.subscriber_positions].length}
              onChange={toggleSelectAll}
              style={{ cursor: "pointer" }}
              title="Select all accounts"
            />
            <span style={{ fontSize: "0.75rem", color: "var(--text-secondary)", whiteSpace: "nowrap" }}>
              {selectedAccountIds.size > 0 ? `${selectedAccountIds.size} selected` : "Select"}
            </span>
            <Button
              size="small"
              variant="accent"
              disabled={isConvertingDust}
              onClick={handleConvertSelectedDust}
              title="Convert dust for selected accounts"
            >
              {isConvertingDust ? "..." : "✨ Convert Selected"}
            </Button>
            <Button
              size="small"
              variant="secondary"
              disabled={isConvertingDust}
              onClick={handleConvertAllDust}
              title="Convert dust for ALL accounts"
            >
              {isConvertingDust ? "..." : "✨ Convert All"}
            </Button>
          </div>
        )}
      </div>

      {isLoading && (
        <div style={{ display: "flex", justifyContent: "center", alignItems: "center", padding: "3rem", gap: "0.75rem", color: "var(--text-secondary)" }}>
          <div style={{
            width: "24px",
            height: "24px",
            border: "3px solid var(--border)",
            borderTopColor: "var(--accent)",
            borderRadius: "50%",
            animation: "spin 0.8s linear infinite",
          }} />
          <span>Loading positions...</span>
        </div>
      )}

      {error && (
        <div style={{ padding: "1rem", backgroundColor: "rgba(239, 68, 68, 0.1)", borderRadius: "0.5rem", marginBottom: "1rem" }}>
          <strong>Error:</strong> {error}
        </div>
      )}

      {dustConvertResult && (
        <div style={{
          padding: "1rem",
          borderRadius: "0.5rem",
          marginBottom: "1rem",
          backgroundColor: dustConvertResult.success ? "rgba(16, 185, 129, 0.1)" : "rgba(239, 68, 68, 0.1)",
          border: `1px solid ${dustConvertResult.success ? "#10b981" : "#ef4444"}`,
        }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
            <div>
              <strong style={{ color: dustConvertResult.success ? "#10b981" : "#ef4444" }}>
                {dustConvertResult.success ? "✅ Dust Conversion Complete" : "❌ Dust Conversion Failed"}
              </strong>
              <p style={{ margin: "0.25rem 0", fontSize: "0.85rem", color: "var(--text-secondary)" }}>
                {dustConvertResult.message}
              </p>
              <div style={{ display: "flex", gap: "0.75rem", marginTop: "0.5rem", fontSize: "0.8rem" }}>
                <span>Accounts: <strong>{dustConvertResult.total_accounts}</strong></span>
                <span style={{ color: "#10b981" }}>Succeeded: <strong>{dustConvertResult.successful_accounts}</strong></span>
                <span style={{ color: "#ef4444" }}>Failed: <strong>{dustConvertResult.failed_accounts}</strong></span>
              </div>
              <div style={{ display: "flex", gap: "0.75rem", marginTop: "0.35rem", fontSize: "0.8rem" }}>
                <span style={{ color: "#10b981" }}>
                  Coins converted: <strong>{dustConvertResult.total_coins_converted}</strong>
                  {" "}(~${dustConvertResult.total_converted_usdt_value.toFixed(4)})
                </span>
                <span style={{ color: "#f59e0b" }}>
                  Coins remaining: <strong>{dustConvertResult.total_coins_remaining}</strong>
                  {" "}(~${dustConvertResult.total_remaining_usdt_value.toFixed(4)})
                </span>
              </div>
              {dustConvertResult.results.length > 0 && (
                <details style={{ marginTop: "0.5rem", fontSize: "0.8rem" }}>
                  <summary style={{ cursor: "pointer", color: "var(--text-secondary)" }}>
                    View account details ({dustConvertResult.results.length})
                  </summary>
                  <div style={{ marginTop: "0.5rem", maxHeight: "220px", overflowY: "auto" }}>
                    {dustConvertResult.results.map((r) => (
                      <div key={r.account_id} style={{
                        padding: "0.4rem",
                        marginBottom: "0.25rem",
                        borderRadius: "0.25rem",
                        backgroundColor: "var(--background-secondary)",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}>
                        <span style={{ display: "flex", flexDirection: "column", gap: "0.15rem" }}>
                          <span>
                            {r.account_nickname || r.account_id}
                          </span>
                          <span style={{ fontSize: "0.72rem", color: "var(--text-secondary)" }}>
                            {r.coins_converted > 0 && (
                              <span style={{ color: "#10b981", marginRight: "0.5rem" }}>
                                {r.coins_converted} converted (~${r.converted_usdt_value.toFixed(4)})
                              </span>
                            )}
                            {r.coins_remaining > 0 && (
                              <span style={{ color: "#f59e0b" }}>
                                {r.coins_remaining} remaining (~${r.remaining_usdt_value.toFixed(4)})
                              </span>
                            )}
                          </span>
                        </span>
                        <span style={{
                          padding: "0.1rem 0.4rem",
                          borderRadius: "0.25rem",
                          fontSize: "0.7rem",
                          backgroundColor: r.success ? "rgba(16, 185, 129, 0.2)" : "rgba(239, 68, 68, 0.2)",
                          color: r.success ? "#10b981" : "#ef4444",
                        }}>
                          {r.success ? "OK" : r.error || "Failed"}
                        </span>
                      </div>
                    ))}
                  </div>
                </details>
              )}
            </div>
            <button
              onClick={clearDustResult}
              style={{
                background: "none",
                border: "none",
                cursor: "pointer",
                fontSize: "1.2rem",
                color: "var(--text-secondary)",
                padding: "0.25rem",
              }}
            >
              ✕
            </button>
          </div>
        </div>
      )}

      {data && (
        <>
          {renderStats()}

          {(() => {
            const fd = getFilteredData();
            if (!fd) return null;
            const showAdmin = viewMode === "all" || viewMode === "admin";
            const showSubscribers = viewMode === "all" || viewMode === "subscribers";
            const showStuck = viewMode === "all" || viewMode === "stuck";
            const hasAnyData = fd.admin_positions.length > 0 || fd.subscriber_positions.length > 0 || fd.stuck_positions.length > 0;

            return (
              <>
                {groupedMode === "grouped" && (
                  <div>
                    {showAdmin && fd.admin_positions.length > 0 && (
                      <div style={{ marginBottom: "1.5rem" }}>
                        <h3 style={{ marginBottom: "0.5rem", display: "flex", alignItems: "center", gap: "0.5rem" }}>
                          👤 Admin Positions ({fd.admin_positions.length})
                        </h3>
                        {fd.admin_positions.map((section) => renderUserSection(section, "admin"))}
                      </div>
                    )}

                    {showSubscribers && fd.subscriber_positions.length > 0 && (
                      <div style={{ marginBottom: "1.5rem" }}>
                        <h3 style={{ marginBottom: "0.5rem", display: "flex", alignItems: "center", gap: "0.5rem" }}>
                          👥 Subscriber Positions ({fd.subscriber_positions.length})
                        </h3>
                        {fd.subscriber_positions.map((section) => renderUserSection(section, "subscriber"))}
                      </div>
                    )}

                    {showStuck && fd.stuck_positions.length > 0 && (
                      <div style={{ marginBottom: "1.5rem" }}>
                        <h3 style={{ marginBottom: "0.5rem", display: "flex", alignItems: "center", gap: "0.5rem", color: "var(--error)" }}>
                          ⚠️ Stuck Positions ({fd.stuck_positions.length})
                        </h3>
                        {renderStuckPositions(fd.stuck_positions)}
                      </div>
                    )}

                    {!hasAnyData && (
                      <div style={{ padding: "2rem", textAlign: "center", color: "var(--text-secondary)" }}>
                        {symbolFilter || statusFilter ? "No positions match the current filters" : "No open positions found"}
                      </div>
                    )}
                  </div>
                )}

                {groupedMode === "flat" && (
                  <div style={{ overflowX: "auto" }}>
                    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.85rem" }}>
                      <thead>
                        <tr style={{ backgroundColor: "var(--background-secondary)", borderBottom: "1px solid var(--border)" }}>
                          <th style={{ padding: "0.5rem", textAlign: "left" }}>User</th>
                          <th style={{ padding: "0.5rem", textAlign: "left" }}>Type</th>
                          <th style={{ padding: "0.5rem", textAlign: "left" }}>Symbol</th>
                          <th style={{ padding: "0.5rem", textAlign: "center" }}>Linked</th>
                          <th style={{ padding: "0.5rem", textAlign: "right" }}>Entry</th>
                          <th style={{ padding: "0.5rem", textAlign: "right" }}>Qty</th>
                          <th style={{ padding: "0.5rem", textAlign: "right" }}>PnL</th>
                          <th style={{ padding: "0.5rem", textAlign: "center" }}>Status</th>
                          <th style={{ padding: "0.5rem", textAlign: "right" }}>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {[
                          ...(showAdmin ? fd.admin_positions : []),
                          ...(showSubscribers ? fd.subscriber_positions : []),
                        ].map((section) =>
                          section.positions.map((pos) => (
                            <tr key={`${section.account_id}-${pos.position_id}`} style={{ borderBottom: "1px solid var(--border)" }}>
                              <td style={{ padding: "0.5rem" }}>{section.user_email}</td>
                              <td style={{ padding: "0.5rem" }}>
                                <span style={{
                                  padding: "0.15rem 0.5rem",
                                  borderRadius: "1rem",
                                  fontSize: "0.75rem",
                                  backgroundColor: section.account_type === "admin" ? "#3b82f6" : "#10b981",
                                  color: "white",
                                }}>
                                  {section.account_type}
                                </span>
                              </td>
                              <td style={{ padding: "0.5rem", fontWeight: "bold" }}>{pos.symbol}</td>
                              <td style={{ padding: "0.5rem", textAlign: "center" }}>
                                {pos.linked_signal_id ? (
                                  <span style={{ padding: "0.15rem 0.5rem", borderRadius: "1rem", fontSize: "0.7rem", backgroundColor: "#3b82f6", color: "white", whiteSpace: "nowrap" }}>Yes</span>
                                ) : (
                                  <span style={{ color: "var(--text-secondary)", fontSize: "0.75rem" }}>–</span>
                                )}
                              </td>
                              <td style={{ padding: "0.5rem", textAlign: "right" }}>{(pos.entry_price ?? 0).toFixed(2)}</td>
                              <td style={{ padding: "0.5rem", textAlign: "right" }}>{(pos.quantity ?? 0).toFixed(4)}</td>
                              <td style={{
                                padding: "0.5rem",
                                textAlign: "right",
                                fontWeight: "bold",
                                color: pos.pnl != null ? (pos.pnl >= 0 ? "#10b981" : "#ef4444") : "inherit",
                              }}>
                                {pos.pnl != null ? `${pos.pnl >= 0 ? "+" : ""}${pos.pnl.toFixed(2)}` : "-"}
                              </td>
                              <td style={{ padding: "0.5rem", textAlign: "center" }}>
                                <span style={{
                                  padding: "0.15rem 0.5rem",
                                  borderRadius: "1rem",
                                  fontSize: "0.75rem",
                                  backgroundColor: STATUS_COLORS[pos.status],
                                  color: "white",
                                }}>
                                  {getPositionStatusLabel(pos)}
                                </span>
                                {pos.status_reason && (
                                  <div style={{ marginTop: "0.25rem", fontSize: "0.68rem", color: "var(--text-secondary)" }}>
                                    {pos.status_reason}
                                  </div>
                                )}
                                {(pos.data_sources ?? []).length > 0 && (
                                  <div style={{ marginTop: "0.35rem", display: "flex", gap: "0.2rem", flexWrap: "wrap", justifyContent: "center" }}>
                                    {(pos.data_sources ?? []).map((src: string) => (
                                      <span key={src} style={{
                                        padding: "0.1rem 0.35rem",
                                        borderRadius: "0.25rem",
                                        fontSize: "0.6rem",
                                        backgroundColor: SOURCE_COLORS[src] || "#6b7280",
                                        color: "white",
                                        whiteSpace: "nowrap",
                                        opacity: 0.85,
                                      }}>
                                        {SOURCE_LABELS[src] || src}
                                      </span>
                                    ))}
                                  </div>
                                )}
                              </td>
                              <td style={{ padding: "0.5rem", textAlign: "right" }}>
                                <div style={{ display: "flex", gap: "0.25rem", flexWrap: "wrap", justifyContent: "flex-end" }}>
                                  {pos.available_actions.slice(0, 4).map((action) => (
                                    <Button
                                      key={action}
                                      size="small"
                                      variant={ACTION_COLORS[action] as "primary" | "secondary" | "danger" | "neutral" | "ghost" | "link" | "accent"}
                                      disabled={executingAction !== null}
                                      onClick={() => handleAction(pos, action)}
                                    >
                                      {executingAction?.positionId === pos.position_id && executingAction?.action === action ? "..." : ACTION_LABELS[action]}
                                    </Button>
                                  ))}
                                  {pos.available_actions.length > 2 && (
                                    <span style={{ fontSize: "0.75rem", color: "var(--text-secondary)", alignSelf: "center" }}>
                                      +{pos.available_actions.length - 2}
                                    </span>
                                  )}
                                </div>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                    {!hasAnyData && (
                      <div style={{ padding: "2rem", textAlign: "center", color: "var(--text-secondary)" }}>
                        {symbolFilter || statusFilter ? "No positions match the current filters" : "No open positions found"}
                      </div>
                    )}
                  </div>
                )}
              </>
            );
          })()}
        </>
      )}

      {!data && !isLoading && !error && (
        <div style={{ padding: "2rem", textAlign: "center", color: "var(--text-secondary)" }}>
          Click "⟳ Refresh" to load positions
        </div>
      )}

      {/* Dust Selection Modal */}
      {dustModalOpen && (
        <div
          style={{
            position: "fixed",
            top: 0, left: 0, right: 0, bottom: 0,
            backgroundColor: "rgba(0,0,0,0.5)",
            zIndex: 1000,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
          onClick={(e) => { if (e.target === e.currentTarget) closeDustModal(); }}
        >
          <div
            style={{
              backgroundColor: "var(--background-primary)",
              borderRadius: "0.75rem",
              border: "1px solid var(--border)",
              width: "600px",
              maxWidth: "90vw",
              maxHeight: "80vh",
              display: "flex",
              flexDirection: "column",
              boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
            }}
          >
            <div style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "1rem 1.25rem",
              borderBottom: "1px solid var(--border)",
            }}>
              <strong style={{ fontSize: "1rem" }}>✨ Convert Dust to USDT</strong>
              <button
                onClick={closeDustModal}
                style={{
                  background: "none", border: "none", cursor: "pointer",
                  fontSize: "1.2rem", color: "var(--text-secondary)", padding: "0.25rem",
                }}
              >
                ✕
              </button>
            </div>

            <div style={{ flex: 1, overflowY: "auto", padding: "1rem 1.25rem" }}>
              {dustModalLoading ? (
                <div style={{ padding: "2rem", textAlign: "center", color: "var(--text-secondary)" }}>
                  Loading accounts...
                </div>
              ) : dustModalData ? (
                <>
                  <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginBottom: "1rem", paddingBottom: "0.75rem", borderBottom: "1px solid var(--border)" }}>
                    <input
                      type="checkbox"
                      checked={dustModalData.total_accounts > 0 && dustModalSelected.size === dustModalData.total_accounts}
                      onChange={toggleDustModalSelectAll}
                      style={{ cursor: "pointer" }}
                    />
                    <span style={{ fontSize: "0.85rem", fontWeight: "bold" }}>
                      {dustModalSelected.size > 0
                        ? `${dustModalSelected.size} / ${dustModalData.total_accounts} accounts selected`
                        : "Select All Accounts"}
                    </span>
                  </div>

                  {dustModalData.admins.length > 0 && (
                    <div style={{ marginBottom: "1rem" }}>
                      <h4 style={{
                        fontSize: "0.8rem",
                        textTransform: "uppercase",
                        letterSpacing: "0.05em",
                        color: "#3b82f6",
                        marginBottom: "0.5rem",
                      }}>
                        Admins ({dustModalData.admins.length})
                      </h4>
                      {dustModalData.admins.map((user) => (
                        <UserSelectRow
                          key={user.user_id}
                          user={user}
                          selectedIds={dustModalSelected}
                          onToggle={toggleDustModalAccount}
                        />
                      ))}
                    </div>
                  )}

                  {dustModalData.subscribers.length > 0 && (
                    <div>
                      <h4 style={{
                        fontSize: "0.8rem",
                        textTransform: "uppercase",
                        letterSpacing: "0.05em",
                        color: "#10b981",
                        marginBottom: "0.5rem",
                      }}>
                        Users ({dustModalData.subscribers.length})
                      </h4>
                      {dustModalData.subscribers.map((user) => (
                        <UserSelectRow
                          key={user.user_id}
                          user={user}
                          selectedIds={dustModalSelected}
                          onToggle={toggleDustModalAccount}
                        />
                      ))}
                    </div>
                  )}

                  {dustModalData.total_accounts === 0 && (
                    <div style={{ padding: "2rem", textAlign: "center", color: "var(--text-secondary)" }}>
                      No trading accounts found
                    </div>
                  )}
                </>
              ) : (
                <div style={{ padding: "2rem", textAlign: "center", color: "var(--text-secondary)" }}>
                  Failed to load accounts
                </div>
              )}
            </div>

            <div style={{
              display: "flex",
              justifyContent: "flex-end",
              gap: "0.5rem",
              padding: "1rem 1.25rem",
              borderTop: "1px solid var(--border)",
            }}>
              <Button size="small" variant="secondary" onClick={closeDustModal}>
                Cancel
              </Button>
              <Button
                size="small"
                variant="accent"
                disabled={dustModalSelected.size === 0 || dustModalLoading}
                onClick={handleDustModalConvert}
              >
                ✨ Convert Selected ({dustModalSelected.size})
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function StatCard({ label, value, color }: { label: string; value: number; color: string }) {
  return (
    <div style={{
      padding: "0.75rem 1rem",
      borderRadius: "0.5rem",
      backgroundColor: "var(--background-secondary)",
      border: `2px solid ${color}`,
      minWidth: "100px",
    }}>
      <div style={{ fontSize: "0.75rem", color: "var(--text-secondary)", marginBottom: "0.25rem" }}>{label}</div>
      <div style={{ fontSize: "1.5rem", fontWeight: "bold", color }}>{value}</div>
    </div>
  );
}

function UserSelectRow({
  user,
  selectedIds,
  onToggle,
}: {
  user: DustUserOption;
  selectedIds: Set<string>;
  onToggle: (accountId: string) => void;
}) {
  if (user.accounts.length === 0) return null;
  return (
    <div style={{
      padding: "0.5rem",
      marginBottom: "0.35rem",
      borderRadius: "0.375rem",
      backgroundColor: "var(--background-secondary)",
    }}>
      <div style={{ fontSize: "0.8rem", fontWeight: "bold", marginBottom: "0.25rem" }}>
        {user.name || user.email}
        <span style={{ color: "var(--text-secondary)", fontWeight: "normal", marginLeft: "0.4rem", fontSize: "0.75rem" }}>
          ({user.email})
        </span>
      </div>
      {user.accounts.map((account) => (
        <label
          key={account.account_id}
          style={{
            display: "flex",
            alignItems: "center",
            gap: "0.4rem",
            padding: "0.25rem 0.4rem",
            borderRadius: "0.25rem",
            cursor: "pointer",
            fontSize: "0.8rem",
            marginLeft: "0.5rem",
          }}
        >
          <input
            type="checkbox"
            checked={selectedIds.has(account.account_id)}
            onChange={() => onToggle(account.account_id)}
            style={{ cursor: "pointer" }}
          />
          <span style={{ color: "var(--text-primary)" }}>
            {account.account_nickname || account.account_id}
          </span>
          {account.account_nickname && (
            <span style={{ color: "var(--text-secondary)", fontSize: "0.7rem" }}>
              ({account.account_id})
            </span>
          )}
        </label>
      ))}
    </div>
  );
}
